import { LoadableImage } from "@/components/atoms/LoadableImage"
import { LoadableVideo } from "@/components/atoms/LoadableVideo"
import { ModalCenter } from "@/components/atoms/ModalCenter"
import { DeleteConfirm } from "@/components/molecules/DeleteConfirm"
import { LoadingSpinner } from "@/components/molecules/LoadingSpinner"
import { Scan } from "@/components/scan/Scan"
import { TimelapseFormUpdate } from "@/components/timelapse/TimelapseFormUpdate"
import { TimelapseStatus } from "@/components/timelapse/TimelapseStatus"
import { useScanGetByTimelapse } from "@/lib/api/scan"
import { useTimelapseDelete, useTimelapseGet, useTimelapseUpdate } from "@/lib/api/timelapse"
import { Scan as ScanType } from "@/lib/types/scan"
import { convertTimelapseModel, TimelapseUpdateSchema } from "@/lib/types/timelapse"
import { ActionIcon } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { useNavigate, useParams } from "@tanstack/react-router"
import { FaPencil, FaTrash } from "react-icons/fa6"
import { IoMdQrScanner } from "react-icons/io"
import { Error404 } from "../404"

export const TimelapsesDetail = () => {
  const { id } = useParams({ from: "/public-layout/timelapses/$id" })
  const { data: timelapse, isLoading: isLoadingTimelapse } = useTimelapseGet({ id: Number(id) })

  const { data: scans, isLoading: isLoadingScans } = useScanGetByTimelapse(timelapse ?? { id: 0 })

  const timelapseUpdate = useTimelapseUpdate()
  const timelapseDelete = useTimelapseDelete()

  const [openedEdit, { open: openEdit, close: closeEdit }] = useDisclosure()
  const [openedDelete, { open: openDelete, close: closeDelete }] = useDisclosure()

  const navigate = useNavigate()

  if (isLoadingTimelapse) return <LoadingSpinner />
  if (!timelapse) return <Error404 />

  const handleEdit = (timelapse: TimelapseUpdateSchema) => {
    timelapseUpdate.mutate(timelapse, {
      onSuccess: () => notifications.show({ variant: "success", message: "Timelapse updated" }),
      onSettled: () => closeEdit()
    })
  }

  const handleDeleteInit = () => {
    openDelete()
  }

  const handleDelete = () => {
    timelapseDelete.mutate(timelapse, {
      onSuccess: () => {
        navigate({ to: "/timelapses" });
        notifications.show({ variant: "success", message: "Timelapse deleted" })
      }
    })
  }

  const handleSetCover = (scan: ScanType) => {
    const newTimelapse = convertTimelapseModel(timelapse)
    newTimelapse.coverId = scan.id

    timelapseUpdate.mutate(newTimelapse, {
      onSuccess: () => notifications.show({ variant: "success", message: "Cover image updated" })
    })
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <div>
          <div className="ml-4 mb-2 md:ml-8 md:mb-8 h-16 xs:h-36 md:h-86 aspect-square float-right rounded-2xl overflow-hidden">
            {timelapse.coverId
              ? <LoadableImage src={`/api/scan/${timelapse.coverId}`} />
              : <div className="w-full h-full flex justify-center items-center text-sm text-muted border border-gray-100 rounded-2xl">No cover yet</div>
            }
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
              <span className="font-bold text-2xl">{timelapse.name}</span>
              <TimelapseStatus status={timelapse.status} />
            </div>
            <div className="flex items-center gap2">
              <ActionIcon variant="transparent" color="black" onClick={openEdit} disabled={!!timelapse.deletedAt}>
                <FaPencil />
              </ActionIcon>
              <ActionIcon variant="transparent" color="red" onClick={handleDeleteInit} disabled={!!timelapse.deletedAt}>
                <FaTrash />
              </ActionIcon>
            </div>
          </div>
          <div className="flex gap-1 items-center text-muted mb-4">
            <IoMdQrScanner />
            {timelapse.scanner.name}
          </div>
          <span className="whitespace-pre-wrap">{timelapse.description}</span>
        </div>
        <div className="flex justify-center w-full">
          <div className="rounded-2xl overflow-hidden">
            <LoadableVideo src={`/api/timelapse/timelapse/${timelapse.id}`} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="font-bold">Scans</span>
            <span className="text-muted text-sm">{`(${timelapse.scans})`}</span>
          </div>
          {isLoadingScans ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-4">
              {scans?.map(s => (
                <Scan key={s.id} scan={s} onSetAsCover={handleSetCover} />
              ))}
            </div>
          )}
        </div>
      </div>
      <ModalCenter
        opened={openedEdit}
        onClose={closeEdit}
        title="Edit timelapse"
      >
        <TimelapseFormUpdate timelapse={timelapse} close={closeEdit} onSubmit={handleEdit} />
      </ModalCenter>
      <DeleteConfirm
        opened={openedDelete}
        onClose={closeDelete}
        modalTitle={timelapse.name}
        title="Are you sure you want to delete it"
        onDelete={handleDelete}
      />
    </>
  )
}
